import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, Checkbox,
  Tooltip, Avatar, LinearProgress
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ImportContacts as ImportIcon,
  Send as SendIcon,
  SmartToy as RobotIcon,
  Explore as RadarIcon,
  History as HistoryIcon,
  CloudUpload as UploadIcon,
  Speed as SpeedIcon,
  Storage as DatabaseIcon
} from '@mui/icons-material';

interface ScraperTarget {
  id: number;
  name: string;
  url: string;
  approach: 'cheerio' | 'puppeteer' | 'custom_script';
  custom_script_path: string | null;
  structure_config: string | null;
  chat_history: string | null;
  directions_markdown: string | null;
  last_scraped_at: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  created_at: string;
  legality_status?: string | null;
  population?: number;
  health_status?: string | null;
  comments?: string | null;
}

interface TempLead {
  id: number;
  target_id: number;
  company_name: string | null;
  domain: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  is_duplicate: boolean;
  status: 'pending' | 'imported' | 'ignored';
  created_at: string;
}

interface ScraperLog {
  id: number;
  status: string;
  leads_found: number;
  what_worked: string | null;
  what_failed: string | null;
  scraped_at: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  analysis?: {
    is_scrapable: boolean;
    approach: 'cheerio' | 'puppeteer' | 'custom_script';
    wrapper_selector: string;
    verified_fields: {
      company_name: boolean;
      domain: boolean;
      email: boolean;
      phone: boolean;
      address: boolean;
    };
    sibling_candidates?: Array<{
      state: string;
      url: string;
      verified?: boolean;
    }>;
  };
}

const ScrapingPage: React.FC = () => {
  const [targets, setTargets] = useState<ScraperTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<ScraperTarget | null>(null);
  const [tempLeads, setTempLeads] = useState<TempLead[]>([]);
  const [logs, setLogs] = useState<ScraperLog[]>([]);
  
  // Loading & notification states
  const [loading, setLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Chatbot states
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Modal dialog states
  const [isNewSessionOpen, setIsNewSessionOpen] = useState(false);
  const [newSessionUrl, setNewSessionUrl] = useState('');
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionPrompt, setNewSessionPrompt] = useState('Extract company names, website domains, and emails from this list.');

  const [isPasteHtmlOpen, setIsPasteHtmlOpen] = useState(false);
  const [pastedHtml, setPastedHtml] = useState('');

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [importDestination, setImportDestination] = useState<'company' | 'outreach'>('outreach');
  const [importListName, setImportListName] = useState('Scraper AI Leads');

  const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/scraper`;

  // Stats dashboard states
  interface ScraperStats {
    totalStates: number;
    coveredStates: number;
    totalLeads: number;
    healthStats: { [key: string]: number };
    legalityStats: { [key: string]: number };
    latestActivity: any[];
  }

  const [stats, setStats] = useState<ScraperStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [isHealthChecking, setIsHealthChecking] = useState(false);
  const [isLeadCsvOpen, setIsLeadCsvOpen] = useState(false);
  const [leadCsvFile, setLeadCsvFile] = useState<File | null>(null);
  const [leadCsvUploading, setLeadCsvUploading] = useState(false);
  const [leadCsvResult, setLeadCsvResult] = useState<any>(null);

  // Dashboard & sidebar helper formatters
  const formatPopulation = (pop: number | undefined) => {
    if (!pop) return 'N/A';
    if (pop >= 1000000) return `${(pop / 1000000).toFixed(1)}M`;
    if (pop >= 1000) return `${(pop / 1000).toFixed(0)}K`;
    return pop.toString();
  };

  const getLegalityColor = (status: string | null | undefined) => {
    if (!status) return '#64748b';
    const s = status.toLowerCase();
    if (s.includes('fully legal')) return '#10b981';
    if (s.includes('medical and decriminalized') || s.includes('medical')) return '#06b6d4';
    if (s.includes('decriminalized')) return '#3b82f6';
    if (s.includes('cbd oil')) return '#f59e0b';
    if (s.includes('fully illegal')) return '#ef4444';
    return '#64748b';
  };

  const getHealthStatusIcon = (health: string | null | undefined) => {
    const h = health || 'healthy';
    if (h === 'healthy') {
      return (
        <Tooltip title="Healthy (200 OK)">
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block', flexShrink: 0 }} />
        </Tooltip>
      );
    }
    if (h === 'protected') {
      return (
        <Tooltip title="Protected / 403 (Needs browser engine)">
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block', flexShrink: 0 }} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Broken / Inactive / Outdated">
        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block', flexShrink: 0 }} />
      </Tooltip>
    );
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE}/stats`);
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load scraper stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleRunHealthCheck = async () => {
    setIsHealthChecking(true);
    setSuccess('Asynchronous link check of state URLs started in background...');
    try {
      await fetchWithAuth(`${API_BASE}/targets/health-check`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      // Polling refresh in 3 seconds to see some results
      setTimeout(() => {
        loadTargets();
        setIsHealthChecking(false);
      }, 3000);
    } catch (err: any) {
      setError(`Health check failed: ${err.message}`);
      setIsHealthChecking(false);
    }
  };

  const handleLeadCsvUpload = async () => {
    if (!leadCsvFile) return;
    try {
      setLeadCsvUploading(true);
      setLeadCsvResult(null);
      setError(null);

      // FormData sets its own multipart Content-Type, so this skips fetchWithAuth's JSON header
      const token = localStorage.getItem('ems_access_token') || localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', leadCsvFile);
      const res = await fetch(`${API_BASE}/leads/import-csv`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Lead CSV import failed.');

      setLeadCsvResult(data);
      setSuccess(data.message);
      setLeadCsvFile(null);
      loadStats();
    } catch (err: any) {
      setError(`Lead CSV import failed: ${err.message}`);
    } finally {
      setLeadCsvUploading(false);
    }
  };

  const handleBulkImportTargets = async () => {
    if (!bulkCsvText.trim()) return;
    try {
      setLoading(true);
      setIsBulkImportOpen(false);
      
      const lines = bulkCsvText.split('\n');
      if (lines.length <= 1) throw new Error('CSV must contain headers and at least one data row.');
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/['"]+/g, '').toLowerCase());
      const importedTargets: any[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Simple robust split that handles commas inside quotes
        const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || line.split(',');
        const values = matches.map(v => v.trim().replace(/^"|"$/g, ''));
        
        const obj: any = {};
        headers.forEach((header, index) => {
          if (index < values.length) {
            obj[header] = values[index];
          }
        });
        
        if (obj.name && obj.url) {
          importedTargets.push({
            name: obj.name,
            url: obj.url,
            legality_status: obj.legality_status || obj.legality || 'Unknown',
            population: obj.population ? parseInt(obj.population) : 0,
            comments: obj.comments || obj.comment || '',
            approach: obj.approach || 'cheerio'
          });
        }
      }
      
      if (importedTargets.length === 0) throw new Error('No valid target records found in CSV.');
      
      const res = await fetchWithAuth(`${API_BASE}/targets/bulk-import`, {
        method: 'POST',
        body: JSON.stringify({ targets: importedTargets })
      });
      
      setSuccess(`Bulk import complete! Processed ${importedTargets.length} records (${res.inserted} inserted, ${res.updated} updated).`);
      setBulkCsvText('');
      await loadTargets();
    } catch (err: any) {
      setError(`Bulk target import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper helper to fetch with auth
  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('ems_access_token') || localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    };
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || data.error || 'Server request failed.');
    }
    return res.json();
  };

  useEffect(() => {
    loadTargets();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTarget) {
      loadLeadsAndLogs(selectedTarget.id);
      
      // Load Target Chat History
      if (selectedTarget.chat_history) {
        try {
          if (typeof selectedTarget.chat_history === 'string') {
            setChatHistory(JSON.parse(selectedTarget.chat_history));
          } else {
            setChatHistory(selectedTarget.chat_history);
          }
        } catch (e) {
          setChatHistory(getInitialGreeting(selectedTarget.name));
        }
      } else {
        setChatHistory(getInitialGreeting(selectedTarget.name));
      }
    } else {
      setTempLeads([]);
      setLogs([]);
      setChatHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTarget]);

  // Polling for active running targets
  useEffect(() => {
    let pollInterval: any = null;
    
    if (selectedTarget && selectedTarget.status === 'running') {
      pollInterval = setInterval(async () => {
        try {
          const updatedTargets = await fetchWithAuth(`${API_BASE}/targets`);
          setTargets(updatedTargets);
          
          const found = updatedTargets.find((t: any) => t.id === selectedTarget.id);
          if (found) {
            setSelectedTarget(found);
            const [leadsData, logsData] = await Promise.all([
              fetchWithAuth(`${API_BASE}/leads/${found.id}`),
              fetchWithAuth(`${API_BASE}/logs/${found.id}`)
            ]);
            setTempLeads(leadsData);
            setLogs(logsData);
          }
        } catch (e) {
          console.error("Polling error:", e);
        }
      }, 2000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [selectedTarget, selectedTarget?.status]);


  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, chatLoading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getInitialGreeting = (targetName?: string): ChatMessage[] => [
    {
      role: 'assistant',
      content: targetName
        ? `Hello! I am your AI Scraping Assistant and Co-Pilot for **${targetName}**. 🤖\n\nI can analyze this state's licensing portal, verify what contact details are scrapable, and automatically execute our background crawling engines to harvest leads for you.\n\nTo begin crawling this directory right away, click the green **"Run Scrape"** button in the header above or click below!`
        : `Hello! I am your AI Scraping Assistant and Co-Pilot. 🤖\n\nI can analyze websites, verify what contact details are scrapable, and automatically configure the best parsing tools to harvest leads. \n\n**To start a new session, click "+ New Scraping Session" above or select one from the list!**`
    }
  ];

  const loadTargets = async (targetIdToSync?: number) => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE}/targets`);
      setTargets(data);
      loadStats(); // Auto refresh stats dashboard on target alterations!
      
      const syncId = targetIdToSync || selectedTarget?.id;
      if (syncId) {
        const updated = data.find((t: any) => t.id === syncId);
        if (updated) {
          setSelectedTarget(updated);
        }
      }
    } catch (err: any) {
      setError(`Failed to load scraper targets: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadsAndLogs = async (id: number) => {
    setLeadsLoading(true);
    try {
      const [leadsData, logsData] = await Promise.all([
        fetchWithAuth(`${API_BASE}/leads/${id}`),
        fetchWithAuth(`${API_BASE}/logs/${id}`)
      ]);
      setTempLeads(leadsData);
      setLogs(logsData);
    } catch (err: any) {
      setError(`Failed to load leads or logs: ${err.message}`);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionUrl.trim()) return;
    try {
      const name = newSessionName.trim() || `Scrape: ${getDomainFromUrl(newSessionUrl)}`;
      const initialPrompt = newSessionPrompt.trim() || 'Extract company names, website domains, and emails from this list.';

      const initialHistory: ChatMessage[] = [
        {
          role: 'assistant',
          content: `Created scraping workspace for **${name}**.\nURL: ${newSessionUrl}`
        },
        {
          role: 'user',
          content: initialPrompt
        }
      ];

      const newTarget = await fetchWithAuth(`${API_BASE}/targets`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          url: newSessionUrl,
          approach: 'cheerio',
          chat_history: initialHistory
        })
      });

      setIsNewSessionOpen(false);
      setNewSessionUrl('');
      setNewSessionName('');
      setNewSessionPrompt('Extract company names, website domains, and emails from this list.');
      setSuccess('Session created successfully! Pre-scanning target page now...');
      await loadTargets();
      
      // Auto-select newly created target
      setSelectedTarget(newTarget);
      setChatHistory(initialHistory);
      setChatLoading(true);

      // Trigger instant layout pre-scanning
      const result = await fetchWithAuth(`${API_BASE}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          targetId: newTarget.id,
          messages: initialHistory,
          url: newSessionUrl,
          prompt: initialPrompt
        })
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        analysis: result.status_analysis
      };

      const finalHistory = [...initialHistory, assistantMessage];
      setChatHistory(finalHistory);

      // Save conversation state back to target DB record
      await fetchWithAuth(`${API_BASE}/targets/${newTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          chat_history: finalHistory,
          approach: result.status_analysis?.approach || 'cheerio',
          structure_config: { wrapper_selector: result.status_analysis?.wrapper_selector || 'a' }
        })
      });

      await loadTargets();
    } catch (err: any) {
      setError(`Failed to create session or pre-scan site: ${err.message}`);
    } finally {
      setChatLoading(false);
    }
  };

  const handleDeleteTarget = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this session and its temporary quarantined leads?')) return;
    try {
      await fetchWithAuth(`${API_BASE}/targets/${id}`, { method: 'DELETE' });
      setSuccess('Scraping session deleted.');
      if (selectedTarget?.id === id) {
        setSelectedTarget(null);
      }
      loadTargets();
    } catch (err: any) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleCreateSiblings = async (siblings: any[]) => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`${API_BASE}/targets/create-siblings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siblings: siblings.map(s => ({
            name: `Distru ${s.state} Cannabis Distributor Directory`,
            url: s.url,
            approach: selectedTarget?.approach || 'cheerio'
          }))
        })
      });
      setSuccess(`Successfully registered ${res.created.length} sister state scraping workspaces!`);
      await loadTargets();
    } catch (err: any) {
      setError(`Failed to register sister workspaces: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() || !selectedTarget) return;

    const userMessageText = chatInput;
    setChatInput('');

    const updatedHistory: ChatMessage[] = [
      ...chatHistory,
      { role: 'user', content: userMessageText }
    ];
    setChatHistory(updatedHistory);
    setChatLoading(true);

    try {
      // Execute Chat request against API
      const result = await fetchWithAuth(`${API_BASE}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          targetId: selectedTarget.id,
          messages: updatedHistory,
          url: selectedTarget.url,
          prompt: userMessageText
        })
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        analysis: result.status_analysis
      };

      const finalHistory = [...updatedHistory, assistantMessage];
      setChatHistory(finalHistory);

      // Save conversation state back to target DB record
      await fetchWithAuth(`${API_BASE}/targets/${selectedTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          chat_history: finalHistory,
          approach: result.status_analysis?.approach || selectedTarget.approach,
          structure_config: { wrapper_selector: result.status_analysis?.wrapper_selector || 'a' }
        })
      });

      // Reload targets to capture changed configurations
      loadTargets();

    } catch (err: any) {
      setError(`Assistant failed to reply: ${err.message}`);
    } finally {
      setChatLoading(false);
    }
  };

  const handleParseHtml = async () => {
    if (!pastedHtml.trim() || !selectedTarget) return;
    setChatLoading(true);
    setIsPasteHtmlOpen(false);
    try {
      const result = await fetchWithAuth(`${API_BASE}/parse-raw-html`, {
        method: 'POST',
        body: JSON.stringify({
          targetId: selectedTarget.id,
          htmlContent: pastedHtml
        })
      });

      const successMessage: ChatMessage = {
        role: 'assistant',
        content: `### 🦾 Offline Raw HTML Parse Complete!\n\nI successfully parsed the HTML source code you provided.\n\n* **Leads Extracted**: ${result.count}\n* **Methods Used**: Gemini AI Element-free parse.\n\nCheck the **Leads Board** on the right side of the screen to review and approve the extracted contacts!`
      };

      const updatedHistory: ChatMessage[] = [
        ...chatHistory,
        { role: 'user', content: `[Pasted raw HTML page source: ${pastedHtml.length} characters]` },
        successMessage
      ];
      setChatHistory(updatedHistory);

      // Save conversation state back to target DB record
      await fetchWithAuth(`${API_BASE}/targets/${selectedTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({ chat_history: updatedHistory })
      });

      setPastedHtml('');
      await loadLeadsAndLogs(selectedTarget.id);
      setSuccess(`HTML parsed successfully: found ${result.count} leads.`);
    } catch (err: any) {
      setError(`Parsing failed: ${err.message}`);
    } finally {
      setChatLoading(false);
    }
  };

  const handleRunScrape = async (targetId: number) => {
    console.log('🔮 handleRunScrape triggered for targetId:', targetId);
    try {
      setSuccess('Analyzing selectors and launching live scraper background run...');
      console.log('Sending POST to scraper run endpoint...');
      const runRes = await fetchWithAuth(`${API_BASE}/run/${targetId}`, { method: 'POST' });
      console.log('Scraper run endpoint response:', runRes);
      
      // Auto-reload to mark target status as "running"
      await loadTargets(targetId);

      // Poll leads updates in 6 seconds
      setTimeout(() => {
        console.log('Polling scraper updates after 6s...');
        loadTargets(targetId);
        if (selectedTarget?.id === targetId) {
          loadLeadsAndLogs(targetId);
        }
      }, 6000);
    } catch (err: any) {
      console.error('❌ Scraper trigger error:', err);
      setError(`Scrape execution trigger failed: ${err.message}`);
    }
  };

  const handleLeadSelect = (id: number) => {
    setSelectedLeads(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllLeads = () => {
    const unimportedLeads = tempLeads.filter(l => l.status === 'pending');
    if (selectedLeads.length === unimportedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(unimportedLeads.map(l => l.id));
    }
  };

  const executeBulkImport = async () => {
    try {
      const data = await fetchWithAuth(`${API_BASE}/leads/import`, {
        method: 'POST',
        body: JSON.stringify({
          leadIds: selectedLeads,
          destination: importDestination,
          listName: importListName
        })
      });
      setSuccess(data.message);
      setIsImportOpen(false);
      setSelectedLeads([]);
      if (selectedTarget) {
        loadLeadsAndLogs(selectedTarget.id);
      }
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
    }
  };

  const getDomainFromUrl = (urlStr: string) => {
    try {
      const url = new URL(urlStr.startsWith('http') ? urlStr : `https://${urlStr}`);
      let host = url.hostname.toLowerCase();
      if (host.startsWith('www.')) host = host.substring(4);
      return host;
    } catch (e) {
      return urlStr;
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
      {/* Top Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.025em', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RadarIcon sx={{ color: '#3b82f6', fontSize: 36 }} />
            AI Scraping Console
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Type target directories, state lists, or resource sites. Let AI pre-scan, verify scrapable content, and harvest contacts.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsNewSessionOpen(true)}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.25,
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.4)'
            }}
          >
            New Scraping Session
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ borderRadius: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Visual Analytics Dashboard */}
      {stats && (
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon sx={{ color: '#3b82f6' }} />
              State Directories & Link Health Dashboard
              {statsLoading && <CircularProgress size={14} sx={{ ml: 1, color: '#3b82f6' }} />}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={isHealthChecking}
                onClick={handleRunHealthCheck}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, px: 2, py: 0.75, color: '#3b82f6', borderColor: '#3b82f6', '&:hover': { backgroundColor: '#eff6ff' } }}
              >
                {isHealthChecking ? 'Checking...' : 'Run Link Check'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<UploadIcon />}
                onClick={() => setIsBulkImportOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, px: 2, py: 0.75 }}
              >
                Bulk Import CSV
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<ImportIcon />}
                onClick={() => { setLeadCsvResult(null); setIsLeadCsvOpen(true); }}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, px: 2, py: 0.75 }}
              >
                Import Leads CSV
              </Button>
            </Box>
          </Box>
          
          <Grid container spacing={3.5}>
            {/* National Coverage Meter */}
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #f1f5f9', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', right: -15, bottom: -15, opacity: 0.05, transform: 'scale(2.5)' }}>
                  <SpeedIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Nationwide Registry Coverage
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, mb: 1.5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#0f172a' }}>
                      {stats.coveredStates}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                      / {stats.totalStates} active states
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: '#f1f5f9', borderRadius: '99px', height: 8, overflow: 'hidden' }}>
                    <Box sx={{ width: `${(stats.coveredStates / stats.totalStates * 100) || 0}%`, bgcolor: '#3b82f6', height: '100%', borderRadius: '99px', transition: 'width 0.5s ease-out' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1, fontWeight: 500 }}>
                    {((stats.coveredStates / stats.totalStates * 100) || 0).toFixed(0)}% of US states registered with source directories
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Link Integrity Status */}
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #f1f5f9', height: '100%', position: 'relative' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Directory Link Integrity Health
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h3" sx={{ fontWeight: 900, color: '#10b981' }}>
                        {stats.healthStats.healthy || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                        HEALTHY (200 OK)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #cbd5e1', pl: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                        {stats.healthStats.protected || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700 }}>
                        PROTECTED
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #cbd5e1', pl: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#ef4444' }}>
                        {stats.healthStats.broken || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 700 }}>
                        BROKEN / OUTDATED
                      </Typography>
                    </Box>
                  </Box>

                  {/* Pure CSS Sleek Segmented Health Bar */}
                  <Box sx={{ display: 'flex', width: '100%', height: 8, borderRadius: '99px', overflow: 'hidden' }}>
                    <Box sx={{ width: `${((stats.healthStats.healthy || 0) / stats.totalStates * 100) || 0}%`, bgcolor: '#10b981' }} />
                    <Box sx={{ width: `${((stats.healthStats.protected || 0) / stats.totalStates * 100) || 0}%`, bgcolor: '#f59e0b' }} />
                    <Box sx={{ width: `${((stats.healthStats.broken || 0) / stats.totalStates * 100) || 0}%`, bgcolor: '#ef4444' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1, fontWeight: 500 }}>
                    Weekly link check checks for dead domains and suggests automated replacement
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Unique Leads & Legality Split */}
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #f1f5f9', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', right: -15, bottom: -15, opacity: 0.05, transform: 'scale(2.5)' }}>
                  <DatabaseIcon sx={{ fontSize: 40, color: '#8b5cf6' }} />
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Extracted Active Contacts Pool
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, mb: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#8b5cf6' }}>
                      {stats.totalLeads}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      ▲ +12%
                    </Typography>
                  </Box>
                  
                  {/* Legality Badges Carousel */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                    <Chip size="small" label={`Fully Legal: ${stats.legalityStats['Fully Legal'] || 0}`} sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }} />
                    <Chip size="small" label={`Medical: ${stats.legalityStats['Medical'] || stats.legalityStats['Medical and Decriminalized'] || 0}`} sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, backgroundColor: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc' }} />
                    <Chip size="small" label={`CBD Only: ${stats.legalityStats['CBD Oil Only'] || 0}`} sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fef3c7' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Left Side: Sessions Directory */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: '16px', height: '100%', backgroundColor: '#f8fafc' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#64748b' }} />
              Active Sessions
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={30} /></Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {targets.map(t => {
                  const isSelected = selectedTarget?.id === t.id;
                  const legalityColor = getLegalityColor(t.legality_status || null);
                  return (
                    <Card
                      key={t.id}
                      onClick={() => setSelectedTarget(t)}
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: isSelected ? '#3b82f6' : '#e2e8f0',
                        boxShadow: isSelected ? '0 4px 12px rgba(59, 130, 246, 0.08)' : 'none',
                        borderRadius: '12px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: isSelected ? '#3b82f6' : '#94a3b8',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '85%' }}>
                            {getHealthStatusIcon(t.health_status || null)}
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: isSelected ? '#2563eb' : '#1e293b' }}>
                              {t.name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDeleteTarget(t.id); }}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' }, p: 0.25 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="caption" noWrap sx={{ color: '#64748b', display: 'block', mt: 0.5, pl: 2 }}>
                          {getDomainFromUrl(t.url)}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pl: 2 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {t.legality_status && (
                              <Chip
                                label={t.legality_status}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.55rem',
                                  fontWeight: 800,
                                  backgroundColor: legalityColor + '15',
                                  color: legalityColor,
                                  border: `1px solid ${legalityColor}40`
                                }}
                              />
                            )}
                            {t.population && t.population > 0 ? (
                              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600 }}>
                                Pop: {formatPopulation(t.population)}
                              </Typography>
                            ) : null}
                          </Box>
                          <Chip
                            label={t.status.toUpperCase()}
                            size="small"
                            color={t.status === 'running' ? 'warning' : t.status === 'completed' ? 'success' : t.status === 'failed' ? 'error' : 'default'}
                            sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                {targets.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                      No scrape sessions yet. Click the top button to launch one!
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Center: AI Conversation Console */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', height: '620px', overflow: 'hidden' }}>
            {/* Chat Header */}
            <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fafafa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: selectedTarget ? '#3b82f6' : '#94a3b8', width: 36, height: 36 }}>
                  <RobotIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {selectedTarget ? `${selectedTarget.name} Co-Pilot` : 'AI Scraping Assistant'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: selectedTarget?.status === 'running'
                        ? '#d97706'
                        : selectedTarget?.status === 'completed'
                          ? '#10b981'
                          : selectedTarget?.status === 'failed'
                            ? '#ef4444'
                            : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 600
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: selectedTarget?.status === 'running'
                          ? '#d97706'
                          : selectedTarget?.status === 'completed'
                            ? '#10b981'
                            : selectedTarget?.status === 'failed'
                              ? '#ef4444'
                              : '#64748b',
                        display: 'inline-block'
                      }}
                    ></span>
                    {selectedTarget?.status === 'running'
                      ? 'Scraping in progress...'
                      : selectedTarget?.status === 'completed'
                        ? 'Last crawl successful'
                        : selectedTarget?.status === 'failed'
                          ? 'Last crawl failed'
                          : 'Ready to crawl'}
                  </Typography>
                </Box>
              </Box>
              {selectedTarget && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={selectedTarget.status === 'running'}
                    startIcon={selectedTarget.status === 'running' ? <CircularProgress size={14} color="inherit" /> : <PlayIcon />}
                    onClick={() => handleRunScrape(selectedTarget.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '8px',
                      px: 1.5,
                      background: selectedTarget.status === 'running'
                        ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                        : 'linear-gradient(135deg, #10b981, #059669)',
                      boxShadow: selectedTarget.status === 'running'
                        ? '0 2px 8px rgba(245, 158, 11, 0.25)'
                        : '0 2px 8px rgba(16, 185, 129, 0.25)',
                      '&:hover': {
                        background: selectedTarget.status === 'running' ? '#d97706' : '#059669'
                      }
                    }}
                  >
                    {selectedTarget.status === 'running' ? 'Scraping...' : 'Run Scrape'}
                  </Button>
                  <Chip
                    label={`${selectedTarget.approach.toUpperCase()} Engine`}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#475569' }}
                  />
                </Box>
              )}
            </Box>

            {/* Top Scanning Progress Bar */}
            {selectedTarget?.status === 'running' && (
              <LinearProgress
                color="warning"
                sx={{
                  height: 4,
                  backgroundColor: '#fef3c7',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #f59e0b, #d97706)'
                  }
                }}
              />
            )}

            {/* Chat Message Window */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: '#f1f5f9' }}>
              {chatHistory.map((msg, index) => {
                const isAI = msg.role === 'assistant';
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isAI ? 'flex-start' : 'flex-end',
                      maxWidth: '85%',
                      alignSelf: isAI ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: isAI ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                        backgroundColor: isAI ? '#ffffff' : '#2563eb',
                        color: isAI ? '#1e293b' : '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        border: isAI ? '1px solid #e2e8f0' : 'none'
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {msg.content}
                      </Typography>

                      {isAI && !msg.analysis && selectedTarget && index === 0 && chatHistory.length === 1 && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px dashed #10b981', borderRadius: '12px', backgroundColor: '#f0fdf4' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', textAlign: 'center' }}>
                            {selectedTarget.status === 'running'
                              ? `Extraction is currently active for ${selectedTarget.name}!`
                              : `Ready to extract license leads from ${selectedTarget.name}?`}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={selectedTarget.status === 'running'}
                            startIcon={selectedTarget.status === 'running' ? <CircularProgress size={14} color="inherit" /> : <PlayIcon />}
                            onClick={() => handleRunScrape(selectedTarget.id)}
                            sx={{
                              background: selectedTarget.status === 'running'
                                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                : 'linear-gradient(135deg, #10b981, #059669)',
                              color: '#fff',
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: '8px',
                              px: 3,
                              '&:hover': {
                                background: selectedTarget.status === 'running' ? '#d97706' : '#059669'
                              }
                            }}
                          >
                            {selectedTarget.status === 'running' ? '⚡ Scraping Active...' : '⚡ Run Scraper Session'}
                          </Button>
                        </Box>
                      )}

                      {/* Display structural analysis checklist inside the bubble */}
                      {isAI && msg.analysis && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', minWidth: '260px' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1 }}>
                            🔍 Verified Scrapable Content Checklist:
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {Object.entries(msg.analysis.verified_fields).map(([field, verified]) => (
                              <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ color: verified ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                                  {verified ? '✓' : '✗'}
                                </span>
                                <Typography variant="caption" sx={{ color: '#475569', textTransform: 'capitalize' }}>
                                  {field.replace('_', ' ')}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PlayIcon />}
                              onClick={() => handleRunScrape(selectedTarget!.id)}
                              sx={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: '#fff',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: '8px',
                                px: 2,
                                '&:hover': {
                                  background: '#059669'
                                }
                              }}
                            >
                              Confirm & Run Scrape
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {/* Display identified sibling state directories with 1-click batch activation (Only show active/verified ones!) */}
                      {isAI && msg.analysis && msg.analysis.sibling_candidates && msg.analysis.sibling_candidates.filter(c => c.verified).length > 0 && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', border: '1px dashed #3b82f6', backgroundColor: '#eff6ff', minWidth: '260px' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <RadarIcon sx={{ fontSize: 16 }} /> AI Pattern-Discovered Sibling States:
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                            {msg.analysis.sibling_candidates.filter(c => c.verified).map((cand, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', p: 1, borderRadius: '6px', border: '1px solid #dbeafe' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>
                                    {cand.state}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.65rem', fontWeight: 600 }}>
                                  Verified Active (200 OK)
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => handleCreateSiblings(msg.analysis!.sibling_candidates!.filter(c => c.verified))}
                            sx={{
                              bgcolor: '#3b82f6',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 700,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#2563eb' }
                            }}
                          >
                            ⚡ Deploy Sister Workspaces in 1-Click
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {chatLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, alignSelf: 'flex-start', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px 16px 16px 4px' }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    AI is pre-scanning site layout & analyzing selectors...
                  </Typography>
                </Box>
              )}

              {selectedTarget === null && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, textAlign: 'center', p: 4 }}>
                  <Avatar sx={{ bgcolor: '#e2e8f0', width: 64, height: 64 }}>
                    <RobotIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
                    Welcome to the Scraper Co-Pilot!
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', maxWidth: '300px' }}>
                    Select an active workspace from the left or create a new target URL session to configure your scraper!
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Chat Input Area */}
            <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#ffffff' }}>
              {selectedTarget && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Engine: <strong>{selectedTarget.approach.toUpperCase()}</strong>
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setIsPasteHtmlOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2, px: 1, borderRadius: '6px', color: '#475569', borderColor: '#cbd5e1' }}
                  >
                    🔌 Paste HTML Fallback
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  placeholder={selectedTarget ? "Tell the AI what to harvest..." : "Choose or create a Scraping Session first"}
                  fullWidth
                  size="small"
                  disabled={!selectedTarget || chatLoading}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChatMessage(); } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
                <Button
                  variant="contained"
                  disabled={!selectedTarget || !chatInput.trim() || chatLoading}
                  onClick={handleSendChatMessage}
                  sx={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    minWidth: '50px',
                    p: 1
                  }}
                >
                  <SendIcon />
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Side: Extracted Leads Board & Live Progress Ticker */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Leads Board Paper */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: '350px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                📋 Leads Board
                {tempLeads.length > 0 && (
                  <Chip
                    label={`${tempLeads.length} parsed`}
                    size="small"
                    sx={{ backgroundColor: '#eff6ff', color: '#1e40af', fontWeight: 700, height: 20 }}
                  />
                )}
              </Typography>
              {selectedTarget && tempLeads.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ImportIcon />}
                  disabled={!selectedLeads.length}
                  onClick={() => setIsImportOpen(true)}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                >
                  Transport ({selectedLeads.length})
                </Button>
              )}
            </Box>

            {leadsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={30} /></Box>
            ) : selectedTarget && tempLeads.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: '12px', maxHeight: '350px', overflowY: 'auto' }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedLeads.length === tempLeads.filter(l => l.status === 'pending').length && tempLeads.filter(l => l.status === 'pending').length > 0}
                          onChange={handleSelectAllLeads}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Company / Domain</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Verification</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tempLeads.map(l => (
                      <TableRow key={l.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            disabled={l.status !== 'pending'}
                            checked={selectedLeads.includes(l.id)}
                            onChange={() => handleLeadSelect(l.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {l.company_name || 'Unidentified'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                            {l.domain}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {l.status === 'imported' ? (
                            <Chip label="Imported" size="small" color="secondary" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                          ) : l.is_duplicate ? (
                            <Tooltip title="This domain website already exists in your companies directory or outreach lists.">
                              <Chip label="ALREADY IN DB" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fef2f2', color: '#b91c1c' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Brand new company not found in your stock list!">
                              <Chip label="NEW STOCK LEAD" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#f0fdf4', color: '#15803d' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', px: 2 }}>
                  {selectedTarget ? "Launch a crawl run from the AI chatbot console to view parsed lead lists here." : "Select an active scraping target to inspect its harvested leads."}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Live Progress Ticker & Diagnostics Paper */}
          {selectedTarget && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <RadarIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
                Scraper Run Ticker & Diagnostics
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '200px', overflowY: 'auto', p: 0.5 }}>
                {/* 1. If currently running, show active indicator */}
                {selectedTarget.status === 'running' && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <CircularProgress size={14} sx={{ color: '#d97706', mt: 0.5 }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#b45309', display: 'block' }}>
                        CRAWL RUNNING (CASCADING)...
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        Running automated engine sequence. If Cheerio gets 0 results, it will automatically escalate to Puppeteer and Gemini extractors.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* 2. Show actual logs from database if present */}
                {logs.length > 0 ? (
                  (() => {
                    const latestLog = logs[0];
                    const workedLines = latestLog.what_worked ? latestLog.what_worked.split('\n').filter(Boolean) : [];
                    const failedLines = latestLog.what_failed ? latestLog.what_failed.split('\n').filter(Boolean) : [];
                    
                    return (
                      <>
                        {failedLines.map((line, idx) => (
                          <Box key={`fail-${idx}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', mt: 0.75, flexShrink: 0 }} />
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#b91c1c', display: 'block', fontSize: '0.72rem' }}>
                                TRIAL EXCEPTION
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#475569', display: 'block', fontSize: '0.72rem' }}>
                                {line}
                              </Typography>
                            </Box>
                          </Box>
                        ))}

                        {workedLines.map((line, idx) => (
                          <Box key={`work-${idx}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981', mt: 0.75, flexShrink: 0 }} />
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#047857', display: 'block', fontSize: '0.72rem' }}>
                                STEP WORKED
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#475569', display: 'block', fontSize: '0.72rem' }}>
                                {line}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </>
                    );
                  })()
                ) : (
                  selectedTarget.status !== 'running' && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8' }} />
                      <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                        No crawl jobs executed for this session yet. Hit 'Confirm & Run Scrape' inside the Chat Console to start!
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          )}

          {/* Scraping Playbook & Directions Panel */}
          {selectedTarget && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                border: '1px solid #e2e8f0', 
                borderRadius: '16px', 
                backgroundColor: '#ffffff', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1.5,
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImportIcon sx={{ color: '#10b981', fontSize: 18 }} />
                📖 Scraping Playbook & Directions
              </Typography>
              
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '12px', 
                  border: '1px solid #edf2f7',
                  maxHeight: '300px', 
                  overflowY: 'auto' 
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#334155', 
                    fontSize: '0.8rem', 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: 1.6,
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  {selectedTarget.directions_markdown || (
                    <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>
                      No specific manual instructions documented for this target yet. The default static/dynamic engine auto-extraction rules apply.
                    </span>
                  )}
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* dialog: Create New Session */}
      <Dialog open={isNewSessionOpen} onClose={() => setIsNewSessionOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '480px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>⚡ Start Scraping Workspace</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField
            label="Workspace/Site Name (e.g. Distru CA Directory)"
            fullWidth
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
          />
          <TextField
            label="Target URL"
            placeholder="https://example.com/directory"
            fullWidth
            value={newSessionUrl}
            onChange={(e) => setNewSessionUrl(e.target.value)}
          />
          <TextField
            label="What do you want to scrape?"
            placeholder="e.g. Find all CA distributor names, website domains, and emails from this directory"
            fullWidth
            multiline
            rows={3}
            value={newSessionPrompt}
            onChange={(e) => setNewSessionPrompt(e.target.value)}
            helperText="The AI Chatbot will instantly scan the layout and suggest the best scraping approach for your goal before running."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsNewSessionOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleCreateSession} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '8px' }}>Launch</Button>
        </DialogActions>
      </Dialog>

      {/* dialog: Bulk Import Dialog */}
      <Dialog open={isImportOpen} onClose={() => setIsImportOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '450px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>📨 Transport Verified Leads</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Choose where to transport your selected <strong>{selectedLeads.length}</strong> verified, non-duplicate company leads:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant={importDestination === 'outreach' ? 'contained' : 'outlined'}
              onClick={() => setImportDestination('outreach')}
              fullWidth
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              Outreach Lists
            </Button>
            <Button
              variant={importDestination === 'company' ? 'contained' : 'outlined'}
              onClick={() => setImportDestination('company')}
              fullWidth
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              Main Company Stock
            </Button>
          </Box>

          {importDestination === 'outreach' && (
            <TextField
              label="Outreach Campaign List Name"
              placeholder="e.g. Scraped California Leads"
              fullWidth
              value={importListName}
              onChange={(e) => setImportListName(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsImportOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={executeBulkImport} variant="contained" color="success" sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '8px' }}>
            Execute Transport
          </Button>
        </DialogActions>
      </Dialog>

      {/* dialog: Paste Raw HTML Fallback Dialog */}
      <Dialog open={isPasteHtmlOpen} onClose={() => setIsPasteHtmlOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '600px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          🔌 Paste Webpage HTML Fallback
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            If the automatic scraping engine is blocked by Cloudflare, logins, or anti-bot protections, you can bypass them completely! 
            Open the target site in your browser, copy the raw page source (Inspect Element &rarr; Copy Outer HTML), and paste it below.
          </Typography>
          <TextField
            label="Raw Page HTML Content"
            placeholder="<html><body>... paste outer HTML code here ...</body></html>"
            fullWidth
            multiline
            rows={12}
            value={pastedHtml}
            onChange={(e) => setPastedHtml(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsPasteHtmlOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={handleParseHtml} 
            disabled={!pastedHtml.trim()} 
            variant="contained" 
            color="primary" 
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '8px' }}
          >
            Run Offline AI Parse
          </Button>
        </DialogActions>
      </Dialog>

      {/* dialog: Import Leads CSV (scraper_leads export) */}
      <Dialog open={isLeadCsvOpen} onClose={() => !leadCsvUploading && setIsLeadCsvOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '500px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          📥 Import Leads CSV
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Upload a leads export (e.g. <code>scraper_leads_*.csv</code>). Each row with a <code>company_name</code> is added to the companies table. Companies that already exist by name are skipped.
          </Typography>
          <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={leadCsvUploading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', alignSelf: 'flex-start' }}>
            {leadCsvFile ? 'Change file' : 'Choose CSV file'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                setLeadCsvFile(e.target.files?.[0] || null);
                setLeadCsvResult(null);
                e.target.value = '';
              }}
            />
          </Button>
          {leadCsvFile && (
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
              {leadCsvFile.name} ({(leadCsvFile.size / 1024).toFixed(0)} KB)
            </Typography>
          )}
          {leadCsvUploading && <LinearProgress />}
          {leadCsvResult && (
            <Alert severity="success">
              <div>Inserted: {leadCsvResult.inserted}</div>
              <div>Skipped (already in companies): {leadCsvResult.skippedExisting}</div>
              <div>Skipped (duplicate name in file): {leadCsvResult.skippedDuplicateInFile}</div>
              <div>Skipped (no company name): {leadCsvResult.skippedNoName}</div>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsLeadCsvOpen(false)} disabled={leadCsvUploading} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
          <Button
            onClick={handleLeadCsvUpload}
            disabled={!leadCsvFile || leadCsvUploading}
            variant="contained"
            color="success"
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '8px' }}
          >
            {leadCsvUploading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* dialog: Bulk Import Scraper Targets */}
      <Dialog open={isBulkImportOpen} onClose={() => setIsBulkImportOpen(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '550px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          📥 Bulk Import Scraper Targets (CSV)
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Paste a CSV list of cannabis directories or source state lists below. The importer supports columns: <code>name</code>, <code>url</code>, and optionally <code>legality_status</code>, <code>population</code>, <code>comments</code>.
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', mb: 1 }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#475569', display: 'block', whiteSpace: 'pre' }}>
              {"name,url,legality_status,population,comments\n"}
              {"Nevada Directory,https://ccb.nv.gov/,Fully Legal,3194176,Has license search portal\n"}
              {"Idaho State,https://isp.idaho.gov/,Fully Illegal,1939033,No active program"}
            </Typography>
          </Box>
          <TextField
            label="Raw CSV Data"
            placeholder="name,url,legality_status,population,comments"
            fullWidth
            multiline
            rows={10}
            value={bulkCsvText}
            onChange={(e) => setBulkCsvText(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsBulkImportOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={handleBulkImportTargets} 
            disabled={!bulkCsvText.trim()} 
            variant="contained" 
            color="secondary" 
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '8px' }}
          >
            Import Targets
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScrapingPage;
